// src/features/journal/services/journalService.js
// Journal entry management service

import { collection, query, where, orderBy, onSnapshot, limit, deleteDoc, doc, getDocs, startAfter } from 'firebase/firestore';
import { db, COLLECTIONS } from '@/shared/utils/core/firebase-config';
import localforage from 'localforage';
import { createJournalEntry } from '@/types';
import { trackJournalEntry, trackPhotoJournalEntry } from '@/shared/utils/analytics/analytics';
import logger from '@/shared/utils/core/logger';
import { ocrContextService } from '@/features/ocr/services/ocr-context-service';


// IndexedDB storage for offline entries
const entryStore = localforage.createInstance({
  name: 'journal',
  storeName: 'pendingEntries',
});

/**
 * Subscribe to user's journal entries
 * @param {string} userId - User ID
 * @param {Function} onUpdate - Callback when entries update
 * @param {Function} onError - Callback when error occurs
 * @returns {Function} - Unsubscribe function
 */
export function subscribeToEntries(userId, onUpdate, onError) {
  if (!userId) {
    logger.warn('[JournalService] No user ID provided');
    onUpdate([]);
    return () => {};
  }

  // Limit to 50 entries for better performance
  // Using client-side pagination to show 10 entries per page (5 pages total)
  const q = query(
    collection(db, COLLECTIONS.JOURNAL_ENTRIES),
    where('uid', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(50)
  );

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const remoteEntries = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      onUpdate(remoteEntries);
    },
    (firestoreError) => {
      logger.error('[JournalService] Firestore subscription error:', firestoreError);
      onError(firestoreError);
    }
  );

  return unsubscribe;
}

/**
 * Clear all pending entries from IndexedDB.
 * Call this on logout to prevent entries from leaking to the next user on the same device.
 * @returns {Promise<void>}
 */
export async function clearPendingEntries() {
  try {
    await entryStore.setItem('entries', []);
    logger.debug('[JournalService] Cleared pending entries from IndexedDB');
  } catch (error) {
    logger.error('[JournalService] Failed to clear pending entries:', error);
  }
}

/**
 * Load pending entries from IndexedDB
 * @returns {Promise<Array>} - Array of pending entries
 */
export async function loadPendingEntries() {
  try {
    const pending = (await entryStore.getItem('entries')) || [];
    logger.debug(`[JournalService] Loaded ${pending.length} pending entries from IndexedDB`);
    return pending;
  } catch (error) {
    logger.error('[JournalService] Failed to load pending entries:', error);
    return [];
  }
}

/**
 * Save pending entries to IndexedDB
 * @param {Array} entries - Pending entries to save
 * @returns {Promise<void>}
 */
export async function savePendingEntries(entries) {
  try {
    await entryStore.setItem('entries', entries);
    logger.debug(`[JournalService] Saved ${entries.length} pending entries to IndexedDB`);
  } catch (error) {
    logger.error('[JournalService] Failed to save pending entries:', error);
    throw error;
  }
}

/**
 * Add entry to pending queue
 * @param {Object} entry - Entry to add
 * @returns {Promise<void>}
 */
export async function addToPendingQueue(entry) {
  try {
    const pendingEntries = await loadPendingEntries();
    await savePendingEntries([entry, ...pendingEntries]);
  } catch (error) {
    logger.error('[JournalService] Failed to add to pending queue:', error);
    throw error;
  }
}

/**
 * Sync pending entries to backend API
 * @param {Object} user - Firebase user object
 * @param {Array} pendingEntries - Entries to sync
 * @returns {Promise<Object>} - { successfulSyncs: Array, failedSyncs: Array, permanentFailures: Array }
 *   permanentFailures: entries rejected with non-retryable errors (4xx except 429).
 *   These are removed from IndexedDB and must be removed from UI — they will never succeed on retry.
 *   Each item: { entry, status, errorCode }
 */
export async function syncPendingEntries(user, pendingEntries) {
  if (!user || !navigator.onLine) {
    logger.debug('[JournalService] Cannot sync: offline or no user');
    return { successfulSyncs: [], failedSyncs: pendingEntries, permanentFailures: [] };
  }

  if (pendingEntries.length === 0) {
    logger.debug('[JournalService] No pending entries to sync');
    return { successfulSyncs: [], failedSyncs: [], permanentFailures: [] };
  }

  const token = await user.getIdToken();
  const successfulSyncs = [];
  const failedSyncs = [];
  // Non-retryable rejections (4xx except 429 rate-limit). Not written back to IndexedDB.
  const permanentFailures = [];

  for (const pendingEntry of pendingEntries) {
    // Skip drafts - they should only sync when user explicitly publishes them
    if (pendingEntry.isDraft === true) {
      logger.debug('[JournalService] Skipping draft entry (not syncing):', pendingEntry._tempId);
      failedSyncs.push(pendingEntry); // Keep in IndexedDB as unsync'd
      continue;
    }

    try {
      // Determine which API endpoint to use.
      // On native iOS/Android, relative URLs don't resolve — use absolute Cloud Run URLs.
      const isHandwritten = pendingEntry.mode === 'handwritten' && pendingEntry.imageBase64;
      const endpoint = isHandwritten
        ? (import.meta.env.VITE_CREATE_HANDWRITTEN_ENTRY_URL || '/api/entries/handwritten')
        : (import.meta.env.VITE_CREATE_ENTRY_URL || '/api/entries');

      logger.debug('[JournalService] 🔄 SYNC ATTEMPT:', {
        endpoint,
        entryId: pendingEntry._tempId,
        isHandwritten,
        timestamp: new Date().toISOString()
      });

      // Prepare request body
      const requestBody = isHandwritten
        ? {
            imageBase64: pendingEntry.imageBase64,
            mood: pendingEntry.mood || 'neutral',
            habits: pendingEntry.habits || []
          }
        : {
            content: pendingEntry.content,
            mood: pendingEntry.mood || 'neutral',
            habits: pendingEntry.habits || [],
            weather: pendingEntry.weather,
            isDraft: pendingEntry.isDraft || false,
            wordCount: pendingEntry.wordCount ?? null,
            writingDuration: pendingEntry.writingDuration ?? null
          };

      // Call API
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorCode = errorData.error || 'API request failed';
        // 4xx errors (except 429 rate-limit) are non-retryable: the server will always reject these.
        // Remove from IndexedDB rather than looping forever.
        const isNonRetryable = response.status >= 400 && response.status < 500 && response.status !== 429;
        if (isNonRetryable) {
          logger.warn(`[JournalService] Non-retryable error (${response.status}) for entry ${pendingEntry._tempId}:`, errorCode);
          permanentFailures.push({ entry: pendingEntry, status: response.status, errorCode });
          continue;
        }
        throw new Error(errorCode);
      }

      const data = await response.json();
      logger.debug(`[JournalService] Successfully synced entry via API:`, data.entry?.id);

      successfulSyncs.push(pendingEntry._tempId);

    } catch (apiError) {
      logger.warn('[JournalService] Entry sync failed:', apiError, pendingEntry);
      failedSyncs.push(pendingEntry);
    }
  }

  logger.debug(`[JournalService] Sync complete: ${successfulSyncs.length} succeeded, ${failedSyncs.length} retryable failures, ${permanentFailures.length} permanent failures`);

  // Update IndexedDB with only retryable-failed entries (drafts + transient errors).
  // Permanent failures are intentionally excluded — they must not be retried.
  await savePendingEntries(failedSyncs);

  // Invalidate OCR context cache when new entries are synced
  // This ensures the context service will rebuild with fresh data
  if (successfulSyncs.length > 0 && user?.uid) {
    ocrContextService.invalidateCache(user.uid).catch(err => {
      logger.debug('[JournalService] OCR context cache invalidation skipped:', err);
    });
  }

  return { successfulSyncs, failedSyncs, permanentFailures };
}

/**
 * Create and add journal entry
 * @param {Object} user - Firebase user object
 * @param {Object} entryData - Entry data
 * @returns {Promise<Object>} - Created entry with temp ID
 */
export async function createEntry(user, entryData) {
  if (!user) throw new Error('Authentication required');

  // Generate temporary ID for optimistic update
  const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

  // Validate entry data
  const validatedEntry = createJournalEntry({
    uid: user.uid,
    ...entryData
  });

  // Add temporary ID for local tracking
  // NOTE: Don't set 'id' field - only _tempId. The 'id' should only be set
  // after Firestore assigns a real document ID upon sync.
  const tempEntry = {
    ...validatedEntry,
    _tempId: tempId
    // id is intentionally undefined/missing until synced to Firestore
  };

  // Track entry creation
  if (entryData.photos && entryData.photos.length > 0) {
    trackPhotoJournalEntry();
  } else {
    trackJournalEntry();
  }

  // Save to local storage
  await addToPendingQueue(tempEntry);

  return tempEntry;
}

/**
 * Custom error for offline deletion attempts
 */
export class OfflineDeletionError extends Error {
  constructor() {
    super('Cannot delete synced entries while offline');
    this.name = 'OfflineDeletionError';
  }
}

/**
 * Custom error for entry not found
 */
export class EntryNotFoundError extends Error {
  constructor(entryId) {
    super(`Entry not found: ${entryId}`);
    this.name = 'EntryNotFoundError';
    this.entryId = entryId;
  }
}

/**
 * Delete journal entry (handles both drafts and published entries)
 * @param {string} entryId - Entry ID or _tempId
 * @param {Object} user - Firebase user object
 * @param {Array} allEntries - All current entries (to find the entry)
 * @returns {Promise<{success: boolean, entryId: string, isDraft: boolean, location: string}>}
 */
export async function deleteEntry(entryId, user, allEntries) {
  if (!entryId) throw new Error('Entry ID required');

  // Find entry to determine its type
  const entry = allEntries.find(e => e.id === entryId || e._tempId === entryId);
  if (!entry) throw new EntryNotFoundError(entryId);

  const isDraft = entry.isDraft === true;
  const hasFirestoreId = !!entry.id && !entry.id.startsWith('temp_');

  // CASE 1: Unsynced entry (only in IndexedDB, no Firestore ID) - Always allow
  // This includes drafts and any entries not yet synced to Firestore
  if (!hasFirestoreId) {
    const pendingEntries = await loadPendingEntries();
    const filtered = pendingEntries.filter(e =>
      e._tempId !== entryId && e.id !== entryId
    );
    await savePendingEntries(filtered);

    logger.debug(`[JournalService] Deleted unsynced ${isDraft ? 'draft' : 'entry'} from IndexedDB: ${entryId}`);
    return { success: true, entryId, isDraft, location: 'indexeddb' };
  }

  // CASE 2 & 3: Synced entries (in Firestore) - Require online
  if (!navigator.onLine) {
    throw new OfflineDeletionError();
  }

  if (!user) throw new Error('Authentication required');

  // Delete from Firestore
  const entryRef = doc(db, COLLECTIONS.JOURNAL_ENTRIES, entryId);
  await deleteDoc(entryRef);

  // Also remove from IndexedDB if it exists there
  const pendingEntries = await loadPendingEntries();
  const filtered = pendingEntries.filter(e =>
    e.id !== entryId && e._tempId !== entryId
  );
  if (filtered.length !== pendingEntries.length) {
    await savePendingEntries(filtered);
  }

  logger.debug(`[JournalService] Deleted ${isDraft ? 'draft' : 'entry'} from Firestore: ${entryId}`);
  return { success: true, entryId, isDraft, location: 'firestore' };
}

/**
 * Load older entries using cursor-based pagination
 * @param {string} userId - User ID
 * @param {Timestamp} afterTimestamp - Load entries after this timestamp
 * @param {number} batchSize - Number of entries to load (default 50)
 * @returns {Promise<Array>} - Older entries
 */
export async function loadOlderEntries(userId, afterTimestamp, batchSize = 50) {
  if (!userId || !afterTimestamp) {
    return [];
  }

  try {
    const q = query(
      collection(db, COLLECTIONS.JOURNAL_ENTRIES),
      where('uid', '==', userId),
      orderBy('createdAt', 'desc'),
      startAfter(afterTimestamp),
      limit(batchSize)
    );

    const snapshot = await getDocs(q);
    const entries = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    logger.info(`[JournalService] Loaded ${entries.length} older entries`);
    return entries;

  } catch (error) {
    logger.error('[JournalService] Failed to load older entries:', error);
    throw error;
  }
}
